import React from 'react';

const FilterPanel = ({
  filters,
  onFilterChange,
  availableFilters,
  onClearAll,
  isExpanded,
  onToggleExpanded
}) => {
  const handleCheckboxChange = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];

    onFilterChange(filterType, newValues);
  };

  const handleDateRangeChange = (days) => {
    onFilterChange('dateRange', days);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.assetTypes?.length > 0) count += filters.assetTypes.length;
    if (filters.platforms?.length > 0) count += filters.platforms.length;
    if (filters.currencies?.length > 0) count += filters.currencies.length;
    if (filters.staleness?.length > 0) count += filters.staleness.length;
    if (filters.dateRange) count += 1;
    return count;
  };

  const activeCount = getActiveFilterCount();

  return (
    <div className="filter-panel">
      <div className="filter-header">
        <button
          className="filter-toggle-btn"
          onClick={onToggleExpanded}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M2.5 5h15M6.25 10h7.5M8.75 15h2.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span>Filters</span>
          {activeCount > 0 && (
            <span className="filter-badge">{activeCount}</span>
          )}
          <svg
            className={`filter-chevron ${isExpanded ? 'expanded' : ''}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
        {activeCount > 0 && (
          <button
            className="clear-filters-btn"
            onClick={onClearAll}
          >
            Clear all
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="filter-content">
          {/* Asset Type Filter */}
          <div className="filter-group">
            <h4>Asset Type</h4>
            <div className="filter-options">
              {availableFilters.assetTypes.map(type => (
                <label key={type} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={filters.assetTypes?.includes(type) || false}
                    onChange={() => handleCheckboxChange('assetTypes', type)}
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Platform Filter */}
          {availableFilters.platforms.length > 0 && (
            <div className="filter-group">
              <h4>Platform</h4>
              <div className="filter-options">
                {availableFilters.platforms.map(platform => (
                  <label key={platform} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.platforms?.includes(platform) || false}
                      onChange={() => handleCheckboxChange('platforms', platform)}
                    />
                    <span>{platform}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Currency Filter */}
          {availableFilters.currencies.length > 0 && (
            <div className="filter-group">
              <h4>Currency</h4>
              <div className="filter-options">
                {availableFilters.currencies.map(currency => (
                  <label key={currency} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.currencies?.includes(currency) || false}
                      onChange={() => handleCheckboxChange('currencies', currency)}
                    />
                    <span>{currency}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Staleness Filter */}
          <div className="filter-group">
            <h4>Data Freshness</h4>
            <div className="filter-options">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.staleness?.includes('fresh') || false}
                  onChange={() => handleCheckboxChange('staleness', 'fresh')}
                />
                <span>Fresh (&lt; 30 days)</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.staleness?.includes('warning') || false}
                  onChange={() => handleCheckboxChange('staleness', 'warning')}
                />
                <span>Warning (30-60 days)</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.staleness?.includes('critical') || false}
                  onChange={() => handleCheckboxChange('staleness', 'critical')}
                />
                <span>Critical (&gt; 60 days)</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.staleness?.includes('api-priced') || false}
                  onChange={() => handleCheckboxChange('staleness', 'api-priced')}
                />
                <span>API-Priced (Always fresh)</span>
              </label>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="filter-group">
            <h4>Acquired In</h4>
            <div className="filter-options">
              <label className="filter-radio">
                <input
                  type="radio"
                  name="dateRange"
                  checked={!filters.dateRange}
                  onChange={() => handleDateRangeChange(null)}
                />
                <span>All time</span>
              </label>
              <label className="filter-radio">
                <input
                  type="radio"
                  name="dateRange"
                  checked={filters.dateRange === 30}
                  onChange={() => handleDateRangeChange(30)}
                />
                <span>Last 30 days</span>
              </label>
              <label className="filter-radio">
                <input
                  type="radio"
                  name="dateRange"
                  checked={filters.dateRange === 90}
                  onChange={() => handleDateRangeChange(90)}
                />
                <span>Last 90 days</span>
              </label>
              <label className="filter-radio">
                <input
                  type="radio"
                  name="dateRange"
                  checked={filters.dateRange === 365}
                  onChange={() => handleDateRangeChange(365)}
                />
                <span>Last year</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Active Filter Badges */}
      {activeCount > 0 && (
        <div className="active-filters">
          {filters.assetTypes?.map(type => (
            <span key={`asset-${type}`} className="filter-tag">
              {type}
              <button onClick={() => handleCheckboxChange('assetTypes', type)}>×</button>
            </span>
          ))}
          {filters.platforms?.map(platform => (
            <span key={`platform-${platform}`} className="filter-tag">
              {platform}
              <button onClick={() => handleCheckboxChange('platforms', platform)}>×</button>
            </span>
          ))}
          {filters.currencies?.map(currency => (
            <span key={`currency-${currency}`} className="filter-tag">
              {currency}
              <button onClick={() => handleCheckboxChange('currencies', currency)}>×</button>
            </span>
          ))}
          {filters.staleness?.map(level => (
            <span key={`staleness-${level}`} className="filter-tag">
              {level === 'fresh' ? 'Fresh' :
               level === 'warning' ? 'Warning' :
               level === 'critical' ? 'Critical' : 'API-Priced'}
              <button onClick={() => handleCheckboxChange('staleness', level)}>×</button>
            </span>
          ))}
          {filters.dateRange && (
            <span className="filter-tag">
              Last {filters.dateRange} days
              <button onClick={() => handleDateRangeChange(null)}>×</button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
